import { Link } from "react-router-dom";
import { Building2 } from "lucide-react";
import { EntityCard } from "./common/EntityCard";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader } from "./ui/card";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export interface Company {
  id: string;
  name: string;
  logo_url?: string;
  address?: string;
  assignee?: string;
  notes?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  vat_number?: string;
  is_dummy_data?: boolean;
  jobCount?: number;
}

interface CompanyCardProps {
  company: Company;
  className?: string;
}

export const CompanyCard = ({ company, className }: CompanyCardProps) => {
  const { t } = useTranslation();
  return (
    <Card className={cn(
      "overflow-hidden transition-all duration-300 hover:shadow-lg backdrop-blur-sm bg-white/80 border border-white/20",
      "hover:bg-white/90 hover:scale-[1.02] hover:border-white/30",
      "dark:bg-gray-800/80 dark:border-gray-700/20 dark:hover:bg-gray-800/90 dark:hover:border-gray-700/30",
      className
    )}>
      <CardHeader className="pb-2 flex flex-row justify-between items-start">
        <div>
          <Link to={`/companies/${company.id}`} className="hover:underline">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 line-clamp-1">{company.name}</h3>
          </Link>
          {company.jobCount !== undefined && (
            <Badge variant="secondary" className="mt-1 bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
              {t("companies.activeJobs", { count: company.jobCount })}
            </Badge>
          )}
        </div>
        
        {company.logo_url && (
          <div className="h-12 w-12 bg-white/50 dark:bg-gray-700/50 rounded-md flex items-center justify-center overflow-hidden ring-1 ring-gray-200/50 dark:ring-gray-600/50 backdrop-blur-sm">
            <img 
              src={company.logo_url} 
              alt={`${company.name} logo`} 
              className="h-10 w-10 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}
      </CardHeader>
      
      <CardContent className="pt-2">
        <div className="space-y-3 text-sm">
          {company.vat_number && (
            <div>
              <span className="font-medium text-gray-500 dark:text-gray-400">VAT Number</span>
              <p className="text-gray-700 dark:text-gray-300">{company.vat_number}</p>
            </div>
          )}
          
          {company.address && (
            <div>
              <span className="font-medium text-gray-500 dark:text-gray-400">{t("companies.address")}</span>
              <p className="text-gray-700 dark:text-gray-300 line-clamp-1">{company.address}</p>
            </div>
          )}
          
          {company.assignee && (
            <div>
              <span className="font-medium text-gray-500 dark:text-gray-400">{t("companies.assignee")}</span>
              <p className="text-gray-700 dark:text-gray-300">{company.assignee}</p>
            </div>
          )}
          
          {company.notes && (
            <div>
              <span className="font-medium text-gray-500 dark:text-gray-400">{t("companies.notes")}</span>
              <p className="text-gray-700 dark:text-gray-300 line-clamp-2">{company.notes}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
